import React, { useState, useEffect, useRef } from 'react';
import Yasgui from '@triply/yasgui';
import '@triply/yasgui/build/yasgui.min.css';

function SPARQLQuery() {
  const yasguiRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedExample, setSelectedExample] = useState('');

  const exampleQueries = {
    'Side Effects': `# Find all side effects of a drug
SELECT DISTINCT ?drug ?side_effect ?hpo_id
WHERE {
  ?collection rdfs:label ?drug .
  FILTER(CONTAINS(LCASE(?drug), "metformin"))
  
  ?assoc a sk:SideEffect ;
         sk:refersToDrug ?collection ;
         sk:hasSideEffect ?phenotype .
  
  ?phenotype rdfs:label ?side_effect .
  BIND(STRAFTER(STR(?phenotype), "http://purl.obolibrary.org/obo/") AS ?hpo_id)
}
ORDER BY ?side_effect
LIMIT 100`,

    'Drug-Drug Contraindications': `# Find unique drug-drug contraindications
SELECT DISTINCT ?drug1 ?drug2
WHERE {
  ?assoc a sk:DrugContraindication ;
         sk:refersToDrug ?coll1 ;
         sk:isContraindicatedWithDrug ?coll2 .
  
  ?coll1 rdfs:label ?drug1 .
  ?coll2 rdfs:label ?drug2 .
}
ORDER BY ?drug1 ?drug2
LIMIT 50`,

    'Disease Indications': `# Find drugs indicated for a disease
SELECT DISTINCT ?drug ?disease ?mondo_id
WHERE {
  ?assoc a sk:DiseaseIndication ;
         sk:refersToDrug ?collection ;
         sk:isIndicatedForDisease ?disease_entity .
  
  ?disease_entity rdfs:label ?disease .
  ?collection rdfs:label ?drug .
  
  FILTER(CONTAINS(LCASE(?disease), "diabetes"))
  
  BIND(STRAFTER(STR(?disease_entity), "http://purl.obolibrary.org/obo/") AS ?mondo_id)
}
ORDER BY ?drug`,

    'Products by Ingredient': `# Find products containing a specific ingredient
SELECT ?product_name ?collection_label
WHERE {
  BIND(rxnorm:723 as ?ingredient)  # Amoxicillin
  
  ?collection a sk:DrugCollection ;
              sio:SIO_000059 ?ingredient ;
              rdfs:label ?collection_label .
  
  ?product a sio:SIO_010039 ;
           sio:SIO_000028 ?collection ;
           rdfs:label ?product_name .
}
LIMIT 20`,

    'Combination Drugs': `# Find combination drugs with multiple ingredients
SELECT ?collection_label (COUNT(?ingredient) as ?ingredient_count)
WHERE {
  ?collection a sk:DrugCollection ;
              rdfs:label ?collection_label ;
              sio:SIO_000059 ?ingredient .
}
GROUP BY ?collection_label
HAVING (COUNT(?ingredient) > 1)
ORDER BY DESC(?ingredient_count)
LIMIT 20`,

    'Contraindications + Side Effects': `# Find drugs contraindicated in disease with their side effects
SELECT DISTINCT ?drug ?contraindicated_disease ?side_effect
WHERE {
  ?contra a sk:DiseaseContraindication ;
          sk:refersToDrug ?collection ;
          sk:isContraindicatedInDisease ?disease .
  
  ?disease rdfs:label ?contraindicated_disease .
  ?collection rdfs:label ?drug .
  
  ?se a sk:SideEffect ;
      sk:refersToDrug ?collection ;
      sk:hasSideEffect ?phenotype .
  
  ?phenotype rdfs:label ?side_effect .
  
  FILTER(CONTAINS(LCASE(?contraindicated_disease), "liver"))
}
LIMIT 50`,
  };

  useEffect(() => {
    if (containerRef.current && !yasguiRef.current) {
      // Smart Fuseki endpoint detection
      const hostname = window.location.hostname;
      const fusekiEndpoint = hostname === 'sidekick.bio2vec.net'
        ? '/fuseki/sidekick/query'  // Production - relative URL via nginx
        : 'http://localhost:3030/sidekick/query';  // Local development

      yasguiRef.current = new Yasgui(containerRef.current, {
        requestConfig: {
          endpoint: fusekiEndpoint,
        },
        copyEndpointOnNewTab: true,
      });

      // Set initial query with prefixes
      const initialQuery = `PREFIX sio: <http://semanticscience.org/resource/>
PREFIX sk: <http://sidekick.bio2vec.net/>
PREFIX obo: <http://purl.obolibrary.org/obo/>
PREFIX rxnorm: <http://purl.bioontology.org/ontology/RXNORM/>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
PREFIX owl: <http://www.w3.org/2002/07/owl#>
PREFIX dcterms: <http://purl.org/dc/terms/>

# Enter your SPARQL query here
SELECT * WHERE {
  ?s ?p ?o
} LIMIT 10`;

      yasguiRef.current.getTab().setQuery(initialQuery);
    }
  }, []);

  const loadExample = (exampleName) => {
    if (yasguiRef.current && exampleQueries[exampleName]) {
      const prefixes = `PREFIX sio: <http://semanticscience.org/resource/>
PREFIX sk: <http://sidekick.bio2vec.net/>
PREFIX obo: <http://purl.obolibrary.org/obo/>
PREFIX rxnorm: <http://purl.bioontology.org/ontology/RXNORM/>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
PREFIX owl: <http://www.w3.org/2002/07/owl#>
PREFIX dcterms: <http://purl.org/dc/terms/>

`;
      yasguiRef.current.getTab().setQuery(prefixes + exampleQueries[exampleName]);
      setSelectedExample(exampleName);
    }
  };

  return (
    <div className="container">
      <h1 style={{ marginBottom: '1rem' }}>SPARQL Query Interface</h1>
      
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
        Run custom SPARQL queries against the Sidekick knowledge graph. 
        Select an example below or write your own query.
      </p>

      {/* Example Queries */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Example Queries</h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {Object.keys(exampleQueries).map(exampleName => (
            <button
              key={exampleName}
              className={`btn ${selectedExample === exampleName ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => loadExample(exampleName)}
            >
              {exampleName}
            </button>
          ))}
        </div>
      </div>

      {/* YASGUI Editor */}
      <div 
        ref={containerRef} 
        style={{ 
          minHeight: '600px',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          overflow: 'hidden'
        }}
      />

      {/* Help */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginBottom: '0.5rem' }}>Tips</h3>
        <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8', fontSize: '0.875rem' }}>
          <li>All queries must include the appropriate PREFIX declarations (auto-added to examples)</li>
          <li>Use LIMIT to restrict result size for better performance</li>
          <li>Press Ctrl+Space for autocomplete suggestions</li>
          <li>Click on any column header to sort results</li>
          <li>Export results using the download button in the results pane</li>
        </ul>
      </div>

      {/* Common URIs Reference */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginBottom: '0.5rem' }}>Common URIs</h3>
        <table style={{ fontSize: '0.875rem' }}>
          <thead>
            <tr>
              <th>Type</th>
              <th>URI Pattern</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Drug Collection</td>
              <td><code>sk:ingredient_set_*</code></td>
              <td><code>sk:ingredient_set_723</code></td>
            </tr>
            <tr>
              <td>Active Ingredient</td>
              <td><code>rxnorm:*</code></td>
              <td><code>rxnorm:723</code> (amoxicillin)</td>
            </tr>
            <tr>
              <td>Disease</td>
              <td><code>obo:MONDO_*</code></td>
              <td><code>obo:MONDO_0005071</code> (diabetes)</td>
            </tr>
            <tr>
              <td>Phenotype</td>
              <td><code>obo:HP_*</code></td>
              <td><code>obo:HP_0002315</code> (headache)</td>
            </tr>
            <tr>
              <td>Association</td>
              <td><code>sk:assoc_*</code></td>
              <td><code>sk:assoc_abc123</code></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SPARQLQuery;
